import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../lib/supabaseClient';
import { useRegisterEditor } from '../lib/EditorContext';
import { defaultContent } from '../lib/defaultContent';
import { formatLink } from '../lib/formatLink';

const { FiCheck, FiMessageCircle, FiPhone } = FiIcons;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const defaultExpectations = [
  "Comprehensive initial intake to understand your family history and current struggles.",
  "Actionable, step-by-step strategies rather than just 'talk therapy'.",
  "Text/email support between sessions for real-time problem solving.",
  "Focus on parental emotional regulation as the foundation for child behavior."
];

export default function Coaching() {
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchContent() {
      try {
        const { data, error } = await supabase
          .from('page_content')
          .select('content')
          .eq('page_id', 'coaching')
          .single();

        if (error) {
          console.warn('Could not fetch coaching content, using defaults.', error.message);
        } else if (data && data.content) {
          setContent(data.content);
        }
      } catch (err) {
        console.error('Error fetching coaching content:', err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchContent();
  }, []);

  const title = content?.title || "1-on-1 Parent Coaching";
  const subtitle = content?.subtitle || "Sometimes general advice isn't enough. Get personalized, strategic support tailored specifically to your family's unique dynamics and challenges.";

  const expectationsHeading = content?.expectationsHeading || "What to Expect";
  const currentExpectations = content?.expectations || defaultExpectations;

  const discoveryCall = content?.discoveryCall || defaultContent.coaching.discoveryCall;
  const singleFocusSession = content?.singleFocusSession || defaultContent.coaching.singleFocusSession;

  const handleSaveSuccess = (newContent) => {
    setContent(newContent);
  };

  useRegisterEditor(
    'coaching',
    { title, subtitle, expectationsHeading, expectations: currentExpectations, discoveryCall, singleFocusSession },
    handleSaveSuccess,
    'Coaching Page'
  );

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <motion.div initial="hidden" animate="visible" exit="hidden" className="py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl md:text-5xl  font-bold text-[#333] dark:text-[#fff] mb-6">{title}</h1>
          <p className="text-lg text-[#333] dark:text-[#fff]">
            {subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <motion.div variants={fadeUp} className="bg-white dark:bg-brand-slate p-8 md:p-12 rounded-[2rem] shadow-sm border border-sand-100">
            <h2 className="text-2xl  font-bold text-[#333] dark:text-[#fff] mb-6">{expectationsHeading}</h2>
            <ul className="space-y-6">
              {currentExpectations.map((text, i) => (
                <li key={i} className="flex gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-sand-100 flex items-center justify-center mt-1">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-[#333] dark:text-[#fff]" />
                  </div>
                  <span className="text-[#333] dark:text-[#fff] leading-relaxed">{text}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div variants={fadeUp} className="space-y-6">
            <div className="bg-purple-800 rounded-2xl p-8 text-white">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold ">{discoveryCall.title}</h3>
                <span className="text-white font-medium">{discoveryCall.priceText}</span>
              </div>
              <p className="text-sand-100 mb-6 text-sm">{discoveryCall.description}</p>
              <a href={formatLink(discoveryCall.buttonLink)} className="w-full px-10 py-4 bg-purple-300 text-black dark:text-black rounded-full font-medium text-center hover:brightness-110 transition-all shadow-[2px_2px_0px_0px_rgba(45,55,72,1)] border-2 border-brand-darkGray hover:translate-y-1 hover:shadow-none flex items-center justify-center gap-2 block">
                <SafeIcon icon={FiPhone} /> {discoveryCall.buttonText}
              </a>
            </div>

            <div className="bg-white dark:bg-brand-slate rounded-2xl p-8 border border-sand-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold  text-[#333] dark:text-[#fff]">{singleFocusSession.title}</h3>
                <span className="text-[#333] dark:text-[#fff] font-bold">{singleFocusSession.priceText}</span>
              </div>
              <p className="text-[#333] dark:text-[#fff] mb-6 text-sm">{singleFocusSession.description}</p>
              <a href={formatLink(singleFocusSession.buttonLink)} className="w-full px-10 py-4 bg-purple-300 text-black dark:text-black rounded-full font-medium text-center hover:brightness-110 transition-all shadow-[2px_2px_0px_0px_rgba(45,55,72,1)] border-2 border-brand-darkGray hover:translate-y-1 hover:shadow-none flex items-center justify-center gap-2 block">
                <SafeIcon icon={FiMessageCircle} /> {singleFocusSession.buttonText}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}